import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from './OrganizationContext';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Phone, MessageSquare, Voicemail } from 'lucide-react';
import { enableBrowserNotifications, readNotificationSettings } from '@/lib/notificationSettings';

interface NotificationsContextValue {
  permission: NotificationPermission;
  requestPermission: () => Promise<void>;
  isConnected: boolean;
  /** The shared app socket (null until connected). Used by team inbox presence. */
  socket: Socket | null;
}

/** Same as useNotifications but returns null outside the provider (optional consumers). */
export function useOptionalNotifications() {
  return useContext(NotificationsContext) ?? null;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
}

interface NotificationsProviderProps {
  children: ReactNode;
}

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isConnected, setIsConnected] = useState(false);
  const [hasRequestedPermission, setHasRequestedPermission] = useState(false);
  
  const socketRef = useRef<Socket | null>(null);
  const [socketState, setSocketState] = useState<Socket | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    // Audio element created lazily on first play to avoid browser autoplay restrictions
  }, []);

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast({
        title: 'Not Supported',
        description: 'Your browser does not support notifications.',
        variant: 'destructive',
      });
      return;
    }

    if (Notification.permission === 'granted') {
      setPermission('granted');
      return;
    }

    if (Notification.permission === 'denied') {
      toast({
        title: 'Notifications Blocked',
        description: 'Please enable notifications in your browser settings.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        enableBrowserNotifications(localStorage);
        toast({
          title: 'Notifications Enabled',
          description: 'You will now receive browser notifications.',
        });
      } else {
        // The browser can close or quietly block the prompt; say so instead of doing nothing.
        toast({
          title: result === 'denied' ? 'Notifications Blocked' : 'Notifications not enabled',
          description: 'Allow notifications from the lock icon in the address bar, then reload.',
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast({
        title: 'Could not enable notifications',
        description: 'Allow notifications from the lock icon in the address bar, then reload.',
        variant: 'destructive',
      });
    }
  };

  // Read user's notification preferences from localStorage
  const getNotificationSettings = () => {
    return readNotificationSettings(localStorage);
  };

  const showBrowserNotification = (title: string, options: NotificationOptions & { url?: string }) => {
    const settings = getNotificationSettings();

    if (!settings.browser || permission !== 'granted') {
      return;
    }

    try {
      const { url, ...notificationOptions } = options;
      const notification = new Notification(title, {
        ...notificationOptions,
        icon: notificationOptions.icon || '/pwa-192x192.png',
        badge: '/pwa-192x192.png',
        silent: !settings.sound,
        requireInteraction: settings.desktop && (notificationOptions as any).requireInteraction,
      });

      notification.onclick = () => {
        window.focus();
        if (url) {
          window.location.href = url;
        }
        notification.close();
      };

      // Play sound only if user enabled it
      if (settings.sound) {
        if (!audioRef.current) {
          audioRef.current = new Audio('/notification-sound.mp3');
          audioRef.current.volume = 0.5;
        }
        audioRef.current.play().catch(err => console.error('Error playing notification sound:', err));
      }

      // Vibrate on mobile if user enabled it
      if (settings.vibration && 'vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]);
      }
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  };

  const showFallbackToast = (title: string, description: string) => {
    const settings = getNotificationSettings();

    toast({
      title,
      description,
    });

    // Play sound even for toast notifications if user enabled it
    if (settings.sound && audioRef.current) {
      audioRef.current.play().catch(err => console.error('Error playing notification sound:', err));
    }

    // Vibrate on mobile if user enabled it
    if (settings.vibration && 'vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user || !currentOrganization) {
      return;
    }

    // Only show notification prompt if browser supports notifications
    // This prevents the "not supported" error on mobile browsers
    if (!hasRequestedPermission && permission === 'default' && 'Notification' in window) {
      const timer = setTimeout(() => {
        const prompt = toast({
          title: 'Enable Notifications',
          description: 'Get instant alerts for calls, messages, and voicemails.',
          action: (
            <button
              onClick={() => { prompt.dismiss(); void requestPermission(); }}
              className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50"
            >
              Enable
            </button>
          ),
        });
        setHasRequestedPermission(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user, currentOrganization, permission, hasRequestedPermission, toast]);

  useEffect(() => {
    if (!isAuthenticated || !user || !currentOrganization) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    const socket = io({
      auth: {
        token,
      },
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 30000,
      timeout: 10000,
    });

    socketRef.current = socket;
    setSocketState(socket);

    socket.on('connect', () => {
      console.log('📡 [NOTIFICATIONS] Socket.IO connected');
      setIsConnected(true);
      
      // Authenticate with JWT token (server expects 'auth' event, not 'authenticate')
      socket.emit('auth', { token });
    });

    socket.on('authenticated', () => {
      console.log('✅ [NOTIFICATIONS] Socket.IO authenticated');
    });

    socket.on('disconnect', () => {
      if (isConnected) {
        console.log('📡 [NOTIFICATIONS] Socket.IO disconnected');
      }
      setIsConnected(false);
    });

    // Billing webhooks emit this after a plan/status change so feature gating
    // updates without a full page reload (see realTimeEventService.emitCapabilityRefresh).
    // Shared team inbox: a teammate assigned / closed / reopened a thread.
    socket.on('inbox:updated', () => {
      queryClient.invalidateQueries({ queryKey: ['/api/conversations'] });
    });

    socket.on('capabilities:refresh', () => {
      console.log('📡 [NOTIFICATIONS] Capabilities refresh requested');
      queryClient.invalidateQueries({ queryKey: ['/api/capabilities'] });
    });

    socket.on('sms:received', (data: {
      leadId: string;
      from: string;
      to: string;
      messageId: string;
      preview: string;
      timestamp: string;
      leadName?: string;
      /** On-call number: only these people are alerted; everyone's inbox still refreshes. */
      notifyUserIds?: string[];
    }) => {
      console.log('📨 [NOTIFICATIONS] SMS received:', data);
      
      const title = `New SMS from ${data.leadName || data.from}`;
      const body = data.preview;
      const alertMe = !Array.isArray(data.notifyUserIds) || (!!user?.id && data.notifyUserIds.includes(user.id));
      
      if (!alertMe) {
        // Off duty for an on-call number: no alert, the inbox still refreshes below.
      } else if (permission === 'granted') {
        showBrowserNotification(title, {
          body,
          tag: `sms-${data.messageId}`,
          url: '/inbox',
        });
      } else {
        showFallbackToast(title, body);
      }
      
      queryClient.invalidateQueries({ queryKey: ['/api/messages'] });
      queryClient.invalidateQueries({ queryKey: ['/api/conversations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/conversations/unread-count'] });
      // Parse leadId to number to match query key type used by TextInbox
      const numericLeadId = data.leadId ? Number(data.leadId) : undefined;
      if (numericLeadId) {
        queryClient.invalidateQueries({ queryKey: ['/api/timeline', numericLeadId] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    });

    // Listen for message delivery status updates (sent → delivered → failed)
    socket.on('message_status_updated', (data: {
      messageId: number;
      messageSid: string;
      status: string;
      leadId?: number;
      errorMessage?: string;
      deliveredAt?: string;
    }) => {
      console.log('📬 [NOTIFICATIONS] Message status updated:', data.status, data.messageSid);
      if (data.leadId) {
        queryClient.invalidateQueries({ queryKey: ['/api/timeline', Number(data.leadId)] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/conversations'] });
    });

    socket.on('call:ringing', (data: {
      callSid: string;
      from: string;
      to: string;
      leadId?: string;
      leadName?: string;
      timestamp: string;
      /** On-call number: only these people are alerted. */
      notifyUserIds?: string[];
    }) => {
      console.log('📞 [NOTIFICATIONS] Incoming call:', data);

      const title = `Incoming call from ${data.leadName || data.from}`;
      const body = `Calling ${data.to}`;

      if (Array.isArray(data.notifyUserIds) && !(user?.id && data.notifyUserIds.includes(user.id))) {
        // Off duty for this on-call number: no alert (lists still refresh below).
      } else if (permission === 'granted') {
        showBrowserNotification(title, {
          body,
          tag: `call-${data.callSid}`,
          url: '/call-records',
          requireInteraction: true,
        });
      } else {
        showFallbackToast(title, body);
      }

      queryClient.invalidateQueries({ queryKey: ['/api/calls'] });
      queryClient.invalidateQueries({ queryKey: ['/api/call-logs'] });
      queryClient.invalidateQueries({ queryKey: ['/api/call-logs/recent'] });
      if (data.leadId) {
        queryClient.invalidateQueries({ queryKey: ['/api/timeline', Number(data.leadId)] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    });

    socket.on('call:missed', (data: {
      callSid: string;
      from: string;
      to: string;
      leadId?: string;
      leadName?: string;
      timestamp: string;
      /** On-call number: only these people are alerted. */
      notifyUserIds?: string[];
    }) => {
      console.log('📞 [NOTIFICATIONS] Missed call:', data);

      const title = `You missed a call from ${data.leadName || data.from}`;
      const body = `Called ${data.to}`;

      if (Array.isArray(data.notifyUserIds) && !(user?.id && data.notifyUserIds.includes(user.id))) {
        // Off duty for this on-call number: no alert (lists still refresh below).
      } else if (permission === 'granted') {
        showBrowserNotification(title, {
          body,
          tag: `call-${data.callSid}`,
          url: '/call-records',
        });
      } else {
        showFallbackToast(title, body);
      }

      queryClient.invalidateQueries({ queryKey: ['/api/calls'] });
      queryClient.invalidateQueries({ queryKey: ['/api/call-logs'] });
      queryClient.invalidateQueries({ queryKey: ['/api/call-logs/recent'] });
      if (data.leadId) {
        queryClient.invalidateQueries({ queryKey: ['/api/timeline', Number(data.leadId)] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    });

    socket.on('voicemail:new', (data: {
      callSid: string;
      from: string;
      to: string;
      recordingUrl: string;
      duration: number;
      leadId?: string;
      leadName?: string;
      timestamp: string;
      /** On-call number: only these people are alerted. */
      notifyUserIds?: string[];
    }) => {
      console.log('📧 [NOTIFICATIONS] New voicemail:', data);

      const title = `You received a voicemail from ${data.leadName || data.from}`;
      const body = `Duration: ${Math.floor(data.duration / 60)}:${String(data.duration % 60).padStart(2, '0')}`;

      if (Array.isArray(data.notifyUserIds) && !(user?.id && data.notifyUserIds.includes(user.id))) {
        // Off duty for this on-call number: no alert (lists still refresh below).
      } else if (permission === 'granted') {
        showBrowserNotification(title, {
          body,
          tag: `call-${data.callSid}`,
          url: '/call-records',
        });
      } else {
        showFallbackToast(title, body);
      }

      queryClient.invalidateQueries({ queryKey: ['/api/voicemails'] });
      queryClient.invalidateQueries({ queryKey: ['/api/calls'] });
      queryClient.invalidateQueries({ queryKey: ['/api/call-logs'] });
      queryClient.invalidateQueries({ queryKey: ['/api/call-logs/recent'] });
      if (data.leadId) {
        queryClient.invalidateQueries({ queryKey: ['/api/timeline', Number(data.leadId)] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    });

    // A notice addressed to this user alone (emitToUser), e.g. a widget
    // meeting booked onto them (meetingHostNotifyService). The row is already
    // in /api/notifications; this just surfaces it without a refresh.
    socket.on('notification:new', (data: {
      type?: string;
      title: string;
      body: string;
      url?: string;
      bookingId?: string;
    }) => {
      if (permission === 'granted') {
        showBrowserNotification(data.title, {
          body: data.body,
          tag: data.bookingId ? `meeting-${data.bookingId}` : undefined,
          url: data.url || '/',
        });
      } else {
        showFallbackToast(data.title, data.body);
      }
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    });

    socket.on('agent_presence_update', (data: {
      type: 'status_changed' | 'call_count_updated' | 'presence_updated';
      userId: string;
      timestamp: string;
      data: any;
    }) => {
      console.log('👤 [NOTIFICATIONS] Agent presence update:', data);
      
      // Only update cache if this event is for the current user
      if (data.userId !== user?.id) {
        console.log('👤 [NOTIFICATIONS] Ignoring presence update for different user:', data.userId);
        return;
      }
      
      // Only handle known event types that we actively emit
      if (data.type !== 'status_changed' && data.type !== 'call_count_updated') {
        console.log('👤 [NOTIFICATIONS] Ignoring unknown presence event type:', data.type);
        return;
      }
      
      queryClient.setQueryData(['/api/agent-presence'], (oldData: any) => {
        if (!oldData) return oldData;
        
        const updates: any = {};
        
        if (data.type === 'status_changed') {
          updates.status = data.data.status;
          updates.statusMessage = data.data.statusMessage;
        } else if (data.type === 'call_count_updated') {
          updates.currentActiveCalls = data.data.activeCalls;
          updates.totalCallsToday = data.data.totalCallsToday;
        }
        
        return { ...oldData, ...updates, updatedAt: data.timestamp };
      });
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocketState(null);
    };
  }, [isAuthenticated, user, currentOrganization, permission, queryClient, toast]);

  const value: NotificationsContextValue = {
    permission,
    requestPermission,
    isConnected,
    socket: socketState,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
